import { useEffect, useState } from 'react';
import { useAccessToken } from '@features/user';
import APIService from '@services/APIService';

interface PatientListResponse {
  patients: Array<{
    patient_id: string;
    identifier: string;
    first_name: string;
    last_name: string;
  }>;
  total: number;
}

/**
 * Hook to check if the user has created real patients (excluding auto-created JOT-001)
 * Returns true if the user has real patients, false if they only have JOT-001 or no patients
 */
export const useHasRealPatients = () => {
  const { getAccessToken } = useAccessToken();
  const [hasRealPatients, setHasRealPatients] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkPatients = async () => {
      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIGetRequest({
          requestString: '/patient/list?page=1&per_page=5',
          accessToken,
        });

        if (response.ok) {
          const data = await response.value as PatientListResponse;
          
          // Check if user has real patients:
          // - Has 0 patients: no real patients
          // - Has 1 patient with identifier "JOT-001": no real patients  
          // - Otherwise: has real patients
          const hasReal = !(
            data.total === 0 || 
            (data.total === 1 && data.patients[0]?.identifier === 'JOT-001')
          );
          
          setHasRealPatients(hasReal);
        }
      } catch (error) {
        console.error('Error checking patients:', error);
        // Default to false if there's an error
        setHasRealPatients(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkPatients();
  }, [getAccessToken]);

  return { hasRealPatients, isLoading };
};